import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { RecommendationCard } from '../components/RecommendationCard';
export default function AIRecommendationsPage() {
    const [recommendations, setRecommendations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [analyzing, setAnalyzing] = useState(false);
    const [statusFilter, setStatusFilter] = useState('pending');
    const [priorityFilter, setPriorityFilter] = useState('all');
    const [statusMessage, setStatusMessage] = useState('');
    useEffect(() => {
        fetchRecommendations();
    }, [statusFilter, priorityFilter]);
    const fetchRecommendations = async () => {
        try {
            const params = new URLSearchParams();
            if (statusFilter !== 'all')
                params.set('status', statusFilter);
            if (priorityFilter !== 'all')
                params.set('priority', priorityFilter);
            params.set('projectStatus', 'active');
            const response = await fetch(`/api/ai/recommendations?${params}`, {
                credentials: 'include',
            });
            if (response.ok) {
                const data = await response.json();
                setRecommendations(data.recommendations || []);
            }
        }
        catch (error) {
            console.error('Failed to fetch recommendations:', error);
        }
        finally {
            setLoading(false);
        }
    };
    const handleAnalyze = async () => {
        setAnalyzing(true);
        setStatusMessage('');
        try {
            const response = await fetch('/api/ai/analyze', {
                method: 'POST',
                credentials: 'include',
            });
            if (response.ok) {
                const data = await response.json();
                setStatusMessage(data.message);
                await fetchRecommendations();
            }
            else {
                setStatusMessage('Failed to analyze portfolio');
            }
        }
        catch (error) {
            setStatusMessage('Error analyzing portfolio');
        }
        finally {
            setAnalyzing(false);
        }
    };
    const handleAccept = async (id) => {
        try {
            const response = await fetch(`/api/ai/recommendations/${id}/accept`, {
                method: 'POST',
                credentials: 'include',
            });
            if (response.ok) {
                setRecommendations(prev => prev.map(r => r.id === id ? { ...r, status: 'accepted' } : r));
            }
        }
        catch (error) {
            console.error('Failed to accept recommendation:', error);
        }
    };
    const handleReject = async (id, reason) => {
        try {
            const response = await fetch(`/api/ai/recommendations/${id}/reject`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ reason }),
            });
            if (response.ok) {
                setRecommendations(prev => prev.map(r => r.id === id ? { ...r, status: 'rejected' } : r));
            }
        }
        catch (error) {
            console.error('Failed to reject recommendation:', error);
        }
    };
    const counts = {
        all: recommendations.length,
        critical: recommendations.filter(r => r.priority === 'critical').length,
        high: recommendations.filter(r => r.priority === 'high').length,
        medium: recommendations.filter(r => r.priority === 'medium').length,
        low: recommendations.filter(r => r.priority === 'low').length,
    };
    if (loading) {
        return (_jsxs("div", { className: "animate-pulse space-y-4", children: [_jsx("div", { className: "h-8 bg-surface-muted rounded-xl w-1/3" }), _jsx("div", { className: "h-32 bg-surface-muted rounded-xl" }), _jsx("div", { className: "h-32 bg-surface-muted rounded-xl" })] }));
    }
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "flex justify-between items-center mb-6", children: [_jsxs("div", { children: [_jsx("h1", { className: "text-2xl font-bold text-body-strong", children: "AI Recommendations" }), _jsx("p", { className: "text-sm text-body-muted mt-1", children: "Rule-based portfolio analysis with actionable recommendations" })] }), _jsx("button", { onClick: handleAnalyze, disabled: analyzing, className: "flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-pill hover:bg-primary-600 disabled:opacity-50", children: analyzing ? (_jsxs(_Fragment, { children: [_jsx("div", { className: "animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" }), "Analyzing..."] })) : (_jsxs(_Fragment, { children: [_jsx("svg", { className: "w-4 h-4", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" }) }), "Analyze Portfolio"] })) })] }), statusMessage && (_jsx("div", { className: "mb-4 p-3 bg-blue-50 text-info rounded-xl text-sm", children: statusMessage })), _jsxs("div", { className: "grid grid-cols-4 gap-4 mb-6", children: [_jsxs("div", { className: "bg-white rounded-xl shadow-card p-4 text-center", children: [_jsx("div", { className: "text-2xl font-bold text-danger", children: counts.critical }), _jsx("div", { className: "text-xs text-body-muted", children: "Critical" })] }), _jsxs("div", { className: "bg-white rounded-xl shadow-card p-4 text-center", children: [_jsx("div", { className: "text-2xl font-bold text-warning", children: counts.high }), _jsx("div", { className: "text-xs text-body-muted", children: "High" })] }), _jsxs("div", { className: "bg-white rounded-xl shadow-card p-4 text-center", children: [_jsx("div", { className: "text-2xl font-bold text-yellow-600", children: counts.medium }), _jsx("div", { className: "text-xs text-body-muted", children: "Medium" })] }), _jsxs("div", { className: "bg-white rounded-xl shadow-card p-4 text-center", children: [_jsx("div", { className: "text-2xl font-bold text-info", children: counts.low }), _jsx("div", { className: "text-xs text-body-muted", children: "Low" })] })] }), _jsxs("div", { className: "flex gap-4 mb-6", children: [_jsx("div", { className: "flex gap-2", children: ['all', 'pending', 'accepted', 'rejected'].map(s => (_jsx("button", { onClick: () => setStatusFilter(s), className: `px-3 py-1.5 text-sm rounded-pill capitalize ${statusFilter === s
                                ? 'bg-ink text-white border-2 border-ink'
                                : 'bg-white text-body-muted hover:bg-surface-muted border border-gray-200'}`, children: s }, s))) }), _jsx("div", { className: "border-l mx-2" }), _jsx("div", { className: "flex gap-2", children: ['all', 'critical', 'high', 'medium', 'low'].map(p => (_jsx("button", { onClick: () => setPriorityFilter(p), className: `px-3 py-1.5 text-sm rounded-pill capitalize ${priorityFilter === p
                                ? 'bg-ink text-white border-2 border-ink'
                                : 'bg-white text-body-muted hover:bg-surface-muted border border-gray-200'}`, children: p === 'all' ? 'Any priority' : p }, p))) })] }), recommendations.length === 0 ? (_jsxs("div", { className: "bg-white rounded-xl shadow-card p-12 text-center", children: [_jsx("svg", { className: "w-16 h-16 text-gray-300 mx-auto mb-4", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 1.5, d: "M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" }) }), _jsx("h3", { className: "text-lg font-semibold text-body-strong mb-2", children: "No Recommendations" }), _jsx("p", { className: "text-body-muted mb-4", children: "Click \"Analyze Portfolio\" to generate AI recommendations based on your project data." })] })) : (_jsx("div", { className: "space-y-3", children: recommendations.map(rec => (_jsx(RecommendationCard, { recommendation: rec, onAccept: handleAccept, onReject: handleReject }, rec.id))) }))] }));
}
//# sourceMappingURL=AIRecommendationsPage.js.map